/**
 * @author Karthik <karthik@314ecorp.com>
 * @description Jeeves Space
 */

import _ from 'lodash';
import { KeycloakTokenParsed } from 'keycloak-js';

import SpaceAccessDeniedError from './error/SpaceAccessDeniedError';
import { DEFAULT_APP_NAME } from '@admin-vite/utils/constants';

export interface ISpace {
	id: string;
	code: string;
	name: string;
	roles: string[];
	colors?: [string, string];
}

interface InitOptions {
	resourceAccess: ResourceAccess;
	lookup?: Record<string, string>;
	spaceId?: string;
}

type ResourceAccess = KeycloakTokenParsed['resource_access'];

const SS_SPACE_KEY = 'jeeves-space';
const REQUEST_HEADER_SPACE = 'x-jeeves-space';
const DEFAULT_SPACE = window.DEFAULT_SPACE ?? 'jeeves-epic';
const SPACE_PREFIX = `${_.toLower(DEFAULT_APP_NAME)}-`;

const SPACE_COLORS: ISpace['colors'][] = [
	['#1F77B4', '#2090D2'], // Deep Blue
	['#FF7F0E', '#FE8D80'], // Vibrant Orange
	['#2CA02C', '#2CB060'], // Fresh Green
	['#D62728', '#D95694'], // Strong Red
	['#9467BD', '#8490D7'], // Medium Purple
	['#8C564B', '#7F708B'], // Warm Brown
	['#E377C2', '#DA8EE7'], // Pink/Magenta
	['#7F7F7F', '#78A0C8'], // Neutral Gray
	['#BCBD22', '#8EBE86'], // Olive/Yellow-Green
	['#17BECF', '#1FBEEB'], // Teal/Cyan
];

class JeevesSpace {
	private spaces: ISpace[];
	private space: ISpace;
	lookup: Record<string, string>;

	constructor() {
		this.lookup = {};
		this.spaces = [];

		const code = this.getSpaceCode(DEFAULT_SPACE);
		this.space = { id: DEFAULT_SPACE, code, name: _.upperFirst(code), roles: [] };
	}

	private readonly getSpaceCode = (id: string) => {
		return _.replace(id, SPACE_PREFIX, '');
	};

	private readonly isAuthorizedSpace = (spaceId: string): boolean => {
		return Boolean(_.find(this.spaces, { id: spaceId }));
	};

	init(initOptions: InitOptions): ISpace {
		this.lookup = initOptions.lookup ?? {};

		this.spaces = _.chain(initOptions.resourceAccess)
			.keys()
			.filter((client) => _.includes(client, SPACE_PREFIX))
			.orderBy([(client) => (client === DEFAULT_SPACE ? 0 : 1), (client) => client])
			.map((id, idx) => {
				const code = this.getSpaceCode(id);
				return {
					id,
					code,
					name: this.lookup[id] ?? `Unnamed (${code})`,
					colors: SPACE_COLORS[idx % SPACE_COLORS.length],
					roles: initOptions.resourceAccess?.[id]?.roles ?? [],
				};
			})
			.value();

		const spaceId = initOptions.spaceId ?? sessionStorage.getItem(SS_SPACE_KEY) ?? this.space.id;
		const space = _.find(this.spaces, ({ id }) => id === spaceId);

		if (space && this.isAuthorizedSpace(space.id)) {
			this.space = space;
			sessionStorage.setItem(SS_SPACE_KEY, space.id);

			return this.space;
		} else {
			const id = spaceId ?? DEFAULT_SPACE;
			const code = this.getSpaceCode(id);
			this.space = { id, code, name: code, roles: [] };

			throw new SpaceAccessDeniedError({ spaceId });
		}
	}

	getSpaceId() {
		return this.space.id;
	}

	getSpace() {
		return this.space;
	}

	getSpaces() {
		return this.spaces;
	}

	setSpace(code: string) {
		const spaceId = `${SPACE_PREFIX}${code}`;
		const space = _.find(this.spaces, { id: spaceId });
		this.space = space ?? { id: spaceId, code, name: _.upperFirst(code), roles: [] };
		sessionStorage.setItem(SS_SPACE_KEY, spaceId);
	}

	createBasePath(id?: string) {
		const space = _.find(this.spaces, { id }) ?? this.space;
		return `${window.basePath}${space.code}/`;
	}

	showSpaceSelector() {
		return _.chain(this.lookup).keys().without(this.space.id).size().value() > 0;
	}
}

const instance = new JeevesSpace();
export default instance;
export { REQUEST_HEADER_SPACE, DEFAULT_SPACE };
